import { useProductMatrix } from '../contexts/ProductMatrixContext';
import { useTabGeneration } from '../hooks/useTabGeneration';
import { useProductProcessing } from '../hooks/useProductProcessing';
import type { Country } from '../types';
import { PlatformHeader } from './platform/PlatformHeader';
import { TabNavigation } from './platform/TabNavigation';
import { TabTableHeader } from './platform/TabTableHeader';
import { TabContent } from './platform/TabContent';
import { getDisplayName } from '../utils/platformMapping';

type PlatformDetailsViewProps = {
  COUNTRIES: Country[];
  getCountryFlag: (countryName: string) => string;
  lastUpdated?: string | null;
};

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse w-64" />
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse w-24" />
      </div>
      <div className="flex gap-2">
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse w-32" />
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse w-28" />
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse w-36" />
      </div>
      <div className="space-y-3">
        <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse w-full" />
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-16 bg-gray-100 dark:bg-gray-800 rounded-md animate-pulse w-full" />
        ))}
      </div>
    </div>
  );
}

function PlatformDetailsView({ COUNTRIES, getCountryFlag, lastUpdated }: PlatformDetailsViewProps) {
  const {
    selectedPlatform,
    currentView,
    browseMode,
    selectedCountry,
    platformProducts,
    lbm2Products,
    onboardingSystems,
    onboardingColumnGroups,
    platformLoading,
    activeTab,
    setActiveTab,
    tabSearchQuery,
    setTabSearchQuery,
    highlightedProduct,
    isDisclaimerDismissed,
    handleDeviceClick,
    handleProductClick,
    handlePartnerClick,
    handleOnboardingClick,
  } = useProductMatrix();

  const {
    hardwareProducts,
    softwareProducts,
    unifiedVisibleCountries,
    searchVisibleCountries,
    useCompactMode,
    level2Platforms,
    level2SearchResults,
    platformDescription,
  } = useProductProcessing({
    platformProducts,
    selectedPlatform,
    tabSearchQuery,
    products: platformProducts,
    COUNTRIES,
    selectedCountry,
    currentView,
    browseMode,
  });

  const tabs = useTabGeneration({
    selectedPlatform,
    platformProducts,
    products: platformProducts,
    hardwareProducts,
    softwareProducts,
    unifiedVisibleCountries,
    level2Platforms,
    lbm2Products,
    onboardingSystems,
    COUNTRIES,
    browseMode,
    selectedCountry,
  });

  if (!selectedPlatform) return null;

  if (platformLoading) return <LoadingSkeleton />;

  const sharedCountryProps = {
    browseMode,
    selectedCountry,
    COUNTRIES,
    unifiedVisibleCountries,
    highlightedProduct,
  };

  return (
    <div className="pb-[20vh]">
      <div className="relative">
        <div className="space-y-2 px-0 sm:px-6 lg:px-0">
          <PlatformHeader
            platformName={getDisplayName(selectedPlatform)}
            description={platformDescription}
            lastUpdated={lastUpdated}
          />

          {tabs.length > 0 && (
            <>
              <div className={`sticky z-20 bg-background border-b border-border pb-2 -mt-2 ${isDisclaimerDismissed ? 'top-[0px]' : 'top-[0px]'}`}>
                <TabNavigation
                  tabs={tabs}
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  searchQuery={tabSearchQuery}
                  onSearchChange={setTabSearchQuery}
                />
                <div className="hidden lg:block">
                  <TabTableHeader
                    activeTab={activeTab}
                    tabSearchQuery={tabSearchQuery}
                    selectedPlatform={selectedPlatform}
                    searchVisibleCountries={searchVisibleCountries}
                    lbm2Products={lbm2Products}
                    level2Platforms={level2Platforms}
                    hardwareProducts={hardwareProducts}
                    softwareProducts={softwareProducts}
                    {...sharedCountryProps}
                  />
                </div>
              </div>

              <div className="space-y-6">
                <TabContent
                  activeTab={activeTab}
                  tabSearchQuery={tabSearchQuery}
                  selectedPlatform={selectedPlatform}
                  tabs={tabs}
                  searchVisibleCountries={searchVisibleCountries}
                  useCompactMode={useCompactMode}
                  hardwareProducts={hardwareProducts}
                  softwareProducts={softwareProducts}
                  lbm2Products={lbm2Products}
                  level2Platforms={level2Platforms}
                  level2SearchResults={level2SearchResults}
                  platformProducts={platformProducts}
                  onboardingSystems={onboardingSystems}
                  onboardingColumnGroups={onboardingColumnGroups}
                  getCountryFlag={getCountryFlag}
                  onDeviceClick={handleDeviceClick}
                  onProductClick={handleProductClick}
                  onPartnerClick={handlePartnerClick}
                  onOnboardingClick={handleOnboardingClick}
                  {...sharedCountryProps}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PlatformDetailsView;
